import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FileText, Scale, AlertCircle, CheckCircle, XCircle, Shield } from 'lucide-react';
import { DottedPattern } from '../components/DecorativeElements';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const sections = [
    {
      icon: <FileText size={32} />,
      title: 'Acceptance of Terms',
      content: [
        'By accessing and using this website, you accept and agree to be bound by these Terms and Conditions.',
        'If you do not agree with any part of these terms, you must not use our website or services.',
        'We reserve the right to modify these terms at any time, and your continued use constitutes acceptance of such modifications.',
        'These terms apply to all users, including browsers, vendors, customers, and contributors of content.',
      ],
    },
    {
      icon: <Scale size={32} />,
      title: 'Use of Services',
      content: [
        'You may use our services for lawful purposes only and in accordance with these Terms and Conditions.',
        'You agree not to use our services in any way that violates any applicable laws or regulations.',
        'You must not transmit any viruses, malware, or harmful code through our website.',
        'You are responsible for maintaining the confidentiality of your account credentials.',
        'We reserve the right to refuse service, terminate accounts, or remove content at our sole discretion.',
      ],
    },
    {
      icon: <Shield size={32} />,
      title: 'Intellectual Property',
      content: [
        'All content on this website, including text, graphics, logos, images, and software, is the property of Team FullStack or its licensors.',
        'You may not reproduce, distribute, modify, or create derivative works from our content without written permission.',
        'Trademarks, service marks, and logos displayed on the website are our property or the property of their respective owners.',
        'Unauthorized use of our intellectual property may result in legal action.',
      ],
    },
    {
      icon: <AlertCircle size={32} />,
      title: 'Limitation of Liability',
      content: [
        'Team FullStack shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services.',
        'We do not guarantee that our website will be error-free, secure, or continuously available.',
        'Our total liability for any claims shall not exceed the amount you paid for our services.',
        'Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability, so some of the above may not apply to you.',
      ],
    },
    {
      icon: <CheckCircle size={32} />,
      title: 'Service Availability',
      content: [
        'We strive to ensure our website and services are available 24/7, but we do not guarantee uninterrupted access.',
        'We may perform maintenance, updates, or modifications that temporarily affect service availability.',
        'We are not responsible for any downtime caused by factors beyond our control, including internet outages or server issues.',
        'We reserve the right to modify or discontinue any service at any time without prior notice.',
      ],
    },
    {
      icon: <XCircle size={32} />,
      title: 'Termination',
      content: [
        'We may terminate or suspend your access to our services immediately, without prior notice, for any breach of these Terms.',
        'Upon termination, your right to use the service will cease immediately.',
        'All provisions of these Terms that by their nature should survive termination shall survive.',
        'You may terminate your use of our services at any time by discontinuing use of the website.',
      ],
    },
  ];

  return (
    <div className="terms-page">
      <section className="terms-hero">
        <DottedPattern />
        <div className="container">
          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Terms & Conditions
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </section>

      <section className="terms-content" ref={ref}>
        <div className="container">
          <motion.div
            className="terms-intro"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p>
              Welcome to Team FullStack. These Terms and Conditions govern your use of our website and services. 
              Please read these terms carefully before using our website.
            </p>
            <p>
              By accessing or using our website, you agree to comply with and be bound by these Terms and Conditions. 
              If you disagree with any part of these terms, you may not access our website or use our services.
            </p>
          </motion.div>

          {sections.map((section, index) => (
            <motion.div
              key={index}
              className="terms-section"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
            >
              <div className="terms-section-header">
                <div className="terms-section-icon">{section.icon}</div>
                <h2>{section.title}</h2>
              </div>
              <ul className="terms-section-content">
                {section.content.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </motion.div>
          ))}

          <motion.div
            className="terms-contact"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <h2>Contact Information</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <div className="terms-contact-info">
              <p><strong>Email:</strong> <a href="mailto:parth@teamfullstack.in">parth@teamfullstack.in</a></p>
              <p><strong>Phone:</strong> <a href="tel:+917668628401">+91 76686 28401</a></p>
              <p><strong>Address:</strong> Unit G22, Kohinoor City Mall, Kohinoor City, Kurla (West) 400070 Mumbai</p>
            </div>
          </motion.div>

          <motion.div
            className="terms-updates"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <h2>Changes to Terms</h2>
            <p>
              We reserve the right to update or modify these Terms and Conditions at any time. We will notify users of 
              any material changes by posting the new Terms and Conditions on this page and updating the "Last Updated" date. 
              Your continued use of our website after any changes constitutes acceptance of the new Terms and Conditions.
            </p>
          </motion.div>

          <motion.div
            className="terms-governing"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <h2>Governing Law</h2>
            <p>
              These Terms and Conditions shall be governed by and construed in accordance with the laws of India. 
              Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Mumbai, India.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;

